import { useState, useEffect } from 'react';
import { getAllFunds, getStreamsByFund, updateUserDocument } from '../../../services/firestoreService';
import { createUser, updateUserRole } from '../../../services/userService';
import { useAuth } from '../../../hooks/useAuth';
import FormField from '../../shared/Forms/FormField';
import ToggleField from '../../shared/Forms/ToggleField';
import MultiSelect from '../../shared/Forms/MultiSelect';
import Button from '../../shared/UI/Button';

const ALL_ROLES = [
  { value: 'analyst', label: 'Analyst' },
  { value: 'head_of_pipeline', label: 'Head of Pipeline' },
  { value: 'cio', label: 'Chief Investment Officer' },
  { value: 'md', label: 'Managing Director' },
  { value: 'ic_member', label: 'IC Member' },
  { value: 'admin', label: 'Admin' },
];

// Returns roles as a normalised array (handles old role-string documents)
function getRolesArray(user) {
  if (!user) return [];
  if (Array.isArray(user.roles)) return user.roles;
  if (user.role) return [user.role];
  return [];
}

export default function UserForm({ user: existingUser, onSaved, onCancel }) {
  const { user: currentUser } = useAuth();

  const [form, setForm] = useState(existingUser ? {
    ...existingUser,
    roles: getRolesArray(existingUser),
  } : {
    email: '',
    full_name: '',
    roles: [],
    fund_access: [],
    stream_access: [],
    is_active: true,
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [funds, setFunds] = useState([]);
  const [streams, setStreams] = useState([]);

  useEffect(() => {
    getAllFunds().then(list => setFunds(list.filter(f => f.is_active !== false)));
  }, []);

  useEffect(() => {
    if (!form.fund_access?.length) { setStreams([]); return; }
    Promise.all(form.fund_access.map(fid => getStreamsByFund(fid)))
      .then(results => setStreams(results.flat()));
  }, [form.fund_access]);

  const selectedFundsHaveStreams = funds
    .filter(f => form.fund_access?.includes(f.id) && f.has_streams)
    .length > 0;

  const fundOptions = funds.map(f => ({ value: f.id, label: f.name }));
  const streamOptions = streams.map(s => ({ value: s.id, label: s.name }));

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  }

  function validate() {
    const e = {};
    if (!form.full_name?.trim()) e.full_name = 'Full name is required';
    if (!existingUser && !form.email?.trim()) e.email = 'Email is required';
    if (!existingUser && form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      e.email = 'Enter a valid email address';
    }
    if (!form.roles?.length) e.roles = 'At least one role is required';
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    try {
      if (existingUser) {
        const updates = {
          full_name: form.full_name,
          display_name: form.full_name,
          roles: form.roles,
          // Keep legacy role field as first role for backwards compat
          role: form.roles[0] || existingUser.role,
          fund_access: form.fund_access || [],
          stream_access: form.stream_access || [],
          is_active: form.is_active !== false,
        };
        await updateUserDocument({ uid: existingUser.id, updates, updatedBy: currentUser.uid });
        // Update custom claims if roles changed
        const prevRoles = getRolesArray(existingUser);
        const rolesChanged = JSON.stringify([...form.roles].sort()) !== JSON.stringify([...prevRoles].sort());
        if (rolesChanged) {
          await updateUserRole({ uid: existingUser.id, roles: form.roles }).catch(() => {});
        }
      } else {
        await createUser({
          email: form.email,
          fullName: form.full_name,
          role: form.roles[0],    // primary role for Cloud Function
          roles: form.roles,       // full roles array
          fundAccess: form.fund_access || [],
          streamAccess: form.stream_access || [],
        });
      }
      setSuccess(true);
      setTimeout(() => { setSuccess(false); onSaved(); }, 2000);
    } catch (err) {
      setErrors({ _global: err.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      {errors._global && <div className="alert alert-error">{errors._global}</div>}
      {success && (
        <div className="alert alert-success">
          {existingUser
            ? 'User updated successfully!'
            : 'User created. A password setup email will be sent to them.'}
        </div>
      )}

      <div className="form-grid mb-4">
        <FormField label="Full Name" required error={errors.full_name}>
          <input className={`form-input ${errors.full_name ? 'error' : ''}`}
            value={form.full_name} onChange={e => set('full_name', e.target.value)} />
        </FormField>

        {!existingUser && (
          <FormField label="Email Address" required error={errors.email}>
            <input type="email" className={`form-input ${errors.email ? 'error' : ''}`}
              value={form.email} onChange={e => set('email', e.target.value)} />
          </FormField>
        )}

        <div className="form-full">
          <MultiSelect
            label="Roles"
            required
            error={errors.roles}
            hint="Select all roles that apply. A user can hold multiple roles."
            options={ALL_ROLES}
            selected={form.roles || []}
            onChange={v => set('roles', v)}
          />
        </div>

        {/* Selected role tags */}
        {form.roles?.length > 0 && (
          <div className="form-full" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: -8 }}>
            {form.roles.map(r => {
              const label = ALL_ROLES.find(x => x.value === r)?.label || r;
              return (
                <span key={r} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 10px', borderRadius: 20, fontSize: 12,
                  background: 'var(--color-accent-light)', color: 'var(--color-accent-dark)',
                  border: '1px solid var(--color-accent)',
                }}>
                  {label}
                  <button type="button" onClick={() => set('roles', form.roles.filter(x => x !== r))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', color: 'var(--color-accent-dark)', fontWeight: 700, lineHeight: 1 }}>
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        )}

        {existingUser && (
          <ToggleField label="Is Active" checked={form.is_active !== false}
            onChange={v => set('is_active', v)} />
        )}

        <div className="form-full">
          <MultiSelect label="Fund Access" options={fundOptions}
            selected={form.fund_access || []}
            onChange={v => set('fund_access', v)} />
        </div>

        {selectedFundsHaveStreams && (
          <div className="form-full">
            <MultiSelect label="Stream Access" options={streamOptions}
              selected={form.stream_access || []}
              onChange={v => set('stream_access', v)} />
          </div>
        )}
      </div>

      {!existingUser && (
        <div className="alert alert-info">
          A password reset email will be sent to the user so they can set their own password.
        </div>
      )}

      <div className="flex-between">
        <Button variant="secondary" type="button" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" type="submit" loading={saving}>
          {existingUser ? 'Save Changes' : 'Create User'}
        </Button>
      </div>
    </form>
  );
}
